import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  FaBell,
  FaBoxOpen,
  FaBoxes,
  FaCheckCircle,
  FaCoins,
  FaExclamationTriangle,
  FaMapMarkedAlt,
  FaShoppingCart,
  FaSyncAlt,
  FaUserClock,
  FaUsers,
} from "react-icons/fa";

import {
  MdInventory2,
  MdNotificationsActive,
  MdOutlineTrendingUp,
} from "react-icons/md";

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getDashboardMap,
  getDashboardOverview,
  getDashboardTrends,
} from "../api/dashboardApi";

import type {
  DashboardAttentionItem,
  DashboardMapUser,
  DashboardOverview,
  DashboardRange,
  DashboardTrends,
} from "../types/dashboard.types";

import GoogleMapLoader from "../components/GoogleMapLoader";
import UserLocationsMap from "../components/UserLocationsMap";

import "../screenstyles/Homestyle.css";

/* =========================================================
 * Types
 * ======================================================= */

interface ProvinceOption {
  name: string;
  lat: number;
  lng: number;
  zoom: number;
}

interface UserLocation {
  lat: number;
  lng: number;

  locationName: string;

  City: string;

  image: string;

  name: string;

  phone: string;

  points: number;

  about: string;

  accountType: string;
}

/* =========================================================
 * Constants
 * ======================================================= */

const rangeOptions: {
  value: DashboardRange;
  label: string;
}[] = [
  {
    value: "today",
    label: "اليوم",
  },
  {
    value: "7d",
    label: "7 أيام",
  },
  {
    value: "30d",
    label: "30 يوم",
  },
  {
    value: "90d",
    label: "90 يوم",
  },
  {
    value: "year",
    label: "السنة",
  },
];

const iraqProvinces: ProvinceOption[] = [
  {
    name: "بغداد",
    lat: 33.3152,
    lng: 44.3661,
    zoom: 11,
  },
  {
    name: "البصرة",
    lat: 30.5085,
    lng: 47.7804,
    zoom: 11,
  },
  {
    name: "نينوى",
    lat: 36.34,
    lng: 43.13,
    zoom: 10,
  },
  {
    name: "أربيل",
    lat: 36.1911,
    lng: 44.0092,
    zoom: 11,
  },
  {
    name: "النجف",
    lat: 32.0259,
    lng: 44.3463,
    zoom: 11,
  },
  {
    name: "كربلاء",
    lat: 32.616,
    lng: 44.0249,
    zoom: 11,
  },
  {
    name: "ذي قار",
    lat: 31.0522,
    lng: 46.261,
    zoom: 10,
  },
  {
    name: "ميسان",
    lat: 31.8356,
    lng: 47.1448,
    zoom: 10,
  },
  {
    name: "واسط",
    lat: 32.5147,
    lng: 45.819,
    zoom: 10,
  },
  {
    name: "ديالى",
    lat: 33.7733,
    lng: 44.6056,
    zoom: 10,
  },
  {
    name: "صلاح الدين",
    lat: 34.5975,
    lng: 43.6782,
    zoom: 10,
  },
  {
    name: "الأنبار",
    lat: 33.3757,
    lng: 43.7478,
    zoom: 8,
  },
  {
    name: "بابل",
    lat: 32.4682,
    lng: 44.5508,
    zoom: 10,
  },
  {
    name: "الديوانية",
    lat: 31.9868,
    lng: 44.9215,
    zoom: 10,
  },
  {
    name: "المثنى",
    lat: 31.318,
    lng: 45.2806,
    zoom: 9,
  },
  {
    name: "كركوك",
    lat: 35.4656,
    lng: 44.3804,
    zoom: 10,
  },
  {
    name: "دهوك",
    lat: 36.8671,
    lng: 42.9884,
    zoom: 10,
  },
  {
    name: "السليمانية",
    lat: 35.5558,
    lng: 45.4351,
    zoom: 10,
  },
];

/* =========================================================
 * Helpers
 * ======================================================= */

function formatNumber(
  value: number,
): string {
  return new Intl.NumberFormat(
    "en-US",
  ).format(value);
}

function formatIQD(
  value: number,
): string {
  return `${new Intl.NumberFormat(
    "en-US",
    {
      maximumFractionDigits: 0,
    },
  ).format(value)} د.ع`;
}

function formatPercentage(
  value: number,
): string {
  return `${value.toFixed(1)}%`;
}

function formatGeneratedAt(
  value: string,
): string {
  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "--";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    },
  ).format(date);
}

function getAttentionClass(
  severity:
    DashboardAttentionItem["severity"],
) {
  switch (severity) {
    case "critical":
      return "attention-critical";

    case "warning":
      return "attention-warning";

    default:
      return "attention-info";
  }
}

function mapDashboardUserToLocation(
  user: DashboardMapUser,
): UserLocation {
  return {
    lat:
      user.latitude,

    lng:
      user.longitude,

    locationName:
      user.locationName ?? "",

    City:
      user.city ??
      user.province ??
      "",

    image:
      user.profileImageUrl ?? "",

    name:
      user.fullName ||
      "مستخدم شوف",

    phone:
      user.phoneNumber ??
      user.userId,

    points:
      user.points ?? 0,

    about:
      [
        user.locationName,
        user.city,
        user.province,
      ]
        .filter(Boolean)
        .join(" - "),

    accountType:
      user.accountType ?? "",
  };
}

/* =========================================================
 * Home
 * ======================================================= */

const Home: React.FC = () => {

  const navigate =
  useNavigate();

  const [
    range,
    setRange,
  ] =
    useState<DashboardRange>(
      "7d",
    );

  const [
    dashboard,
    setDashboard,
  ] =
    useState<DashboardOverview | null>(
      null,
    );

  const [
    trends,
    setTrends,
  ] =
    useState<DashboardTrends | null>(
      null,
    );

  const [
    mapUsers,
    setMapUsers,
  ] =
    useState<
      DashboardMapUser[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    refreshing,
    setRefreshing,
  ] =
    useState(false);

  const [
    trendsLoading,
    setTrendsLoading,
  ] =
    useState(false);

  const [
    mapLoading,
    setMapLoading,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState<string | null>(
      null,
    );

  const [
    mapError,
    setMapError,
  ] =
    useState<string | null>(
      null,
    );

  const [
    selectedProvince,
    setSelectedProvince,
  ] =
    useState<ProvinceOption | null>(
      null,
    );

  const [
    mapFilters,
    setMapFilters,
  ] =
    useState<string[]>([
      "منصب",
      "وكيل",
    ]);

  /* =======================================================
   * Overview
   * ===================================================== */

  const loadDashboard =
    useCallback(
      async (
        selectedRange:
          DashboardRange,
        isRefresh = false,
      ) => {
        try {
          if (isRefresh) {
            setRefreshing(
              true,
            );
          } else {
            setLoading(
              true,
            );
          }

          setError(null);

          const data =
            await getDashboardOverview(
              selectedRange,
            );

          setDashboard(
            data,
          );
        } catch (loadError) {
          console.error(
            "Dashboard overview error:",
            loadError,
          );

          setError(
            "تعذر تحميل بيانات لوحة التحكم.",
          );
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
      },
      [],
    );

  /* =======================================================
   * Trends
   * ===================================================== */

  const loadTrends =
    useCallback(
      async (
        selectedRange:
          DashboardRange,
      ) => {
        try {
          setTrendsLoading(
            true,
          );

          const data =
            await getDashboardTrends(
              selectedRange,
            );

          setTrends(
            data,
          );
        } catch (loadError) {
          console.error(
            "Dashboard trends error:",
            loadError,
          );
        } finally {
          setTrendsLoading(
            false,
          );
        }
      },
      [],
    );

  /* =======================================================
   * Map
   * ===================================================== */

  const loadMap =
    useCallback(
      async (
        province?: string,
      ) => {
        try {
          setMapLoading(
            true,
          );

          setMapError(null);

          const data =
            await getDashboardMap({
              province:
                province ||
                undefined,

              limit: 2000,
            });

          setMapUsers(
            data.users,
          );
        } catch (loadError) {
          console.error(
            "Dashboard map error:",
            loadError,
          );

          setMapError(
            "تعذر تحميل مواقع المستخدمين.",
          );
        } finally {
          setMapLoading(
            false,
          );
        }
      },
      [],
    );

  /* =======================================================
   * Initial + range
   * ===================================================== */

  useEffect(
    () => {
      void Promise.all([
        loadDashboard(
          range,
        ),

        loadTrends(
          range,
        ),
      ]);
    },
    [
      range,
      loadDashboard,
      loadTrends,
    ],
  );

  useEffect(
    () => {
      void loadMap(
        selectedProvince?.name,
      );
    },
    [
      selectedProvince,
      loadMap,
    ],
  );

  /* =======================================================
   * Map filters
   * ===================================================== */

  const handleMapFilterChange =
    (
      type: string,
    ) => {
      setMapFilters(
        previous =>
          previous.includes(
            type,
          )
            ? previous.filter(
                item =>
                  item !==
                  type,
              )
            : [
                ...previous,
                type,
              ],
      );
    };

  const userLocations =
    useMemo(
      () =>
        mapUsers
          .filter(
            user =>
              mapFilters.length ===
                0 ||
              (
                user.accountType &&
                mapFilters.includes(
                  user.accountType,
                )
              ),
          )
          .map(
            mapDashboardUserToLocation,
          ),
      [
        mapUsers,
        mapFilters,
      ],
    );

  /* =======================================================
   * KPI cards
   * ===================================================== */

  const mainCards =
    useMemo(
      () => {
        if (!dashboard) {
          return [];
        }

        return [
          {
            key: "users",

            title:
              "إجمالي المستخدمين",

            value:
              formatNumber(
                dashboard.users
                  .total,
              ),

            subtitle:
              `+${formatNumber(
                dashboard.users
                  .newToday,
              )} اليوم`,

            icon:
              <FaUsers />,

            tone:
              "blue",
          },

          {
            key: "active",

            title:
              "نشط الآن",

            value:
              formatNumber(
                dashboard.users
                  .activeNow,
              ),

            subtitle:
              `${formatNumber(
                dashboard.users
                  .activeLast24Hours,
              )} خلال 24 ساعة`,

            icon:
              <FaUserClock />,

            tone:
              "green",
          },

          {
            key: "orders",

            title:
              "إجمالي الطلبات",

            value:
              formatNumber(
                dashboard.orders
                  .total,
              ),

            subtitle:
              `${formatNumber(
                dashboard.orders
                  .incomplete,
              )} غير مكتملة`,

            icon:
              <FaShoppingCart />,

            tone:
              "purple",
          },

          {
            key: "sales",

            title:
              "قيمة الطلبات",

            value:
              formatIQD(
                dashboard.sales
                  .totalOrdersValue,
              ),

            subtitle:
              `${formatIQD(
                dashboard.sales
                  .today,
              )} اليوم`,

            icon:
              <MdOutlineTrendingUp />,

            tone:
              "orange",
          },

          {
            key: "products",

            title:
              "المنتجات",

            value:
              formatNumber(
                dashboard.products
                  .total,
              ),

            subtitle:
              `${formatNumber(
                dashboard.products
                  .outOfStock,
              )} نفد مخزونها`,

            icon:
              <MdInventory2 />,

            tone:
              "cyan",
          },

          {
            key: "serials",

            title:
              "السيريلات",

            value:
              formatNumber(
                dashboard.serials
                  .total,
              ),

            subtitle:
              `${formatNumber(
                dashboard.serials
                  .redeemed,
              )} مستخدمة`,

            icon:
              <FaBoxes />,

            tone:
              "indigo",
          },

          {
            key: "points",

            title:
              "إجمالي النقاط",

            value:
              formatNumber(
                dashboard.points
                  .totalCurrentBalance,
              ),

            subtitle:
              `${formatNumber(
                dashboard.points
                  .transactionsToday,
              )} عملية اليوم`,

            icon:
              <FaCoins />,

            tone:
              "gold",
          },

          {
            key:
              "notifications",

            title:
              "نجاح الإشعارات",

            value:
              formatPercentage(
                dashboard
                  .notifications
                  .successRate,
              ),

            subtitle:
              `${formatNumber(
                dashboard
                  .notifications
                  .today.failed,
              )} فشل اليوم`,

            icon:
              <MdNotificationsActive />,

            tone:
              "rose",
          },
        ];
      },
      [
        dashboard,
      ],
    );

  /* =======================================================
   * Loading
   * ===================================================== */

  if (
    loading &&
    !dashboard
  ) {
    return (
      <main
        className="company-dashboard"
        dir="rtl"
      >
        <section className="dashboard-loading">
          <div className="loading-spinner" />

          <h2>
            جارٍ تحميل لوحة التحكم
          </h2>

          <p>
            يتم جلب أحدث بيانات SHOOF
          </p>
        </section>
      </main>
    );
  }

  if (
    error &&
    !dashboard
  ) {
    return (
      <main
        className="company-dashboard"
        dir="rtl"
      >
        <section className="dashboard-error">
          <FaExclamationTriangle />

          <h2>
            تعذر تحميل لوحة التحكم
          </h2>

          <p>
            {error}
          </p>

          <button
            type="button"
            onClick={() =>
              void loadDashboard(
                range,
              )
            }
          >
            إعادة المحاولة
          </button>
        </section>
      </main>
    );
  }

  if (!dashboard) {
    return null;
  }

  return (
    <main
      className="company-dashboard"
      dir="rtl"
    >
      {/* ===================================================
          Header
      =================================================== */}

      <header className="dashboard-topbar">
        <div className="dashboard-heading">
          <div className="dashboard-heading-badge">
            SHOOF
          </div>

          <div>
            <h1>
              لوحة التحكم
            </h1>

            <p>
              نظرة شاملة على أداء منصة شوف والعمليات اليومية.
            </p>
          </div>
        </div>

        <div className="dashboard-header-actions">
          <div className="dashboard-update-time">
            <span>
              آخر تحديث
            </span>

            <strong>
              {formatGeneratedAt(
                dashboard.generatedAt,
              )}
            </strong>
          </div>

          <button
            type="button"
            className="dashboard-refresh-button"
            disabled={
              refreshing
            }
            onClick={() =>
              void Promise.all([
                loadDashboard(
                  range,
                  true,
                ),
                loadTrends(
                  range,
                ),
                loadMap(
                  selectedProvince
                    ?.name,
                ),
              ])
            }
          >
            <FaSyncAlt
              className={
                refreshing
                  ? "is-spinning"
                  : ""
              }
            />

            {refreshing
              ? "جارٍ التحديث"
              : "تحديث"}
          </button>
        </div>
      </header>

      {/* ===================================================
          Range
      =================================================== */}

      <section className="dashboard-range-section">
        <div>
          <h2>
            الفترة الزمنية
          </h2>

          <p>
            الفترة المستخدمة في مؤشرات النشاط والرسوم البيانية.
          </p>
        </div>

        <div className="dashboard-range-tabs">
          {rangeOptions.map(
            option => (
              <button
                key={
                  option.value
                }
                type="button"
                className={
                  range ===
                  option.value
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setRange(
                    option.value,
                  )
                }
              >
                {option.label}
              </button>
            ),
          )}
        </div>
      </section>

      {/* ===================================================
          KPI
      =================================================== */}

      <section className="dashboard-kpi-grid">
        {mainCards.map(
          card => (
            <article
              key={
                card.key
              }
              className={`dashboard-kpi-card dashboard-kpi-${card.tone}`}
            >
              <div className="dashboard-kpi-icon">
                {card.icon}
              </div>

              <div className="dashboard-kpi-content">
                <span>
                  {card.title}
                </span>

                <strong>
                  {card.value}
                </strong>

                <small>
                  {card.subtitle}
                </small>
              </div>
            </article>
          ),
        )}
      </section>


{/* ===================================================
    Account Upgrade Requests
=================================================== */}

<section className="account-request-grid">
  <button
    type="button"
    className="account-request-card technician-request-card"
    onClick={() =>
      navigate(
        "/technical-join-requests",
      )
    }
  >
    <div className="account-request-icon">
      <FaUserClock />
    </div>

    <div className="account-request-content">
      <span className="account-request-label">
        طلبات التحويل إلى تقني
      </span>

      <strong>
        {formatNumber(
          dashboard.users
            .pendingTechnicianRequests,
        )}
      </strong>

      <small>
        طلب بانتظار المراجعة
      </small>
    </div>

    <div className="account-request-action">
      <span>
        عرض الطلبات
      </span>

      <span className="account-request-arrow">
        ←
      </span>
    </div>
  </button>

  <button
    type="button"
    className="account-request-card agent-request-card"
    onClick={() =>
      navigate(
        "/agent-join-requests",
      )
    }
  >
    <div className="account-request-icon">
      <FaUsers />
    </div>

    <div className="account-request-content">
      <span className="account-request-label">
        طلبات التحويل إلى وكيل
      </span>

      <strong>
        {formatNumber(
          dashboard.users
            .pendingAgentRequests,
        )}
      </strong>

      <small>
        طلب بانتظار المراجعة
      </small>
    </div>

    <div className="account-request-action">
      <span>
        عرض الطلبات
      </span>

      <span className="account-request-arrow">
        ←
      </span>
    </div>
  </button>
</section>
      {/* ===================================================
          Attention + Order Status
      =================================================== */}

      <section className="dashboard-main-grid">
        <article className="dashboard-section-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                يحتاج إلى انتباه
              </h2>

              <p>
                أهم الحالات التي تحتاج متابعة الإدارة.
              </p>
            </div>

            <span className="section-header-icon warning-icon">
              <FaExclamationTriangle />
            </span>
          </div>

          <div className="attention-list">
            {dashboard.attention.length ===
            0 ? (
              <div className="attention-empty">
                <FaCheckCircle />

                <div>
                  <strong>
                    كل شيء جيد
                  </strong>

                  <span>
                    لا توجد حالات حرجة حاليًا.
                  </span>
                </div>
              </div>
            ) : (
              dashboard.attention.map(
                (
                  item,
                  index,
                ) => (
                  <div
                    key={`${item.type}-${index}`}
                    className={`attention-item ${getAttentionClass(
                      item.severity,
                    )}`}
                  >
                    <div className="attention-item-text">
                      <strong>
                        {item.title}
                      </strong>

                      <span>
                        {
                          item.description
                        }
                      </span>
                    </div>

                    <div className="attention-count">
                      {formatNumber(
                        item.count,
                      )}
                    </div>
                  </div>
                ),
              )
            )}
          </div>
        </article>

        <article className="dashboard-section-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                حالة الطلبات
              </h2>

              <p>
                توزيع الطلبات حسب الحالة الحالية.
              </p>
            </div>

            <span className="section-header-icon">
              <FaShoppingCart />
            </span>
          </div>

          <div className="order-status-grid">
            <div>
              <span>
                معلقة
              </span>

              <strong>
                {formatNumber(
                  dashboard.orders
                    .statuses
                    .pending,
                )}
              </strong>
            </div>

            <div>
              <span>
                مؤكدة
              </span>

              <strong>
                {formatNumber(
                  dashboard.orders
                    .statuses
                    .confirmed,
                )}
              </strong>
            </div>

            <div>
              <span>
                قيد المعالجة
              </span>

              <strong>
                {formatNumber(
                  dashboard.orders
                    .statuses
                    .processing,
                )}
              </strong>
            </div>

            <div>
              <span>
                مكتملة
              </span>

              <strong>
                {formatNumber(
                  dashboard.orders
                    .statuses
                    .completed,
                )}
              </strong>
            </div>

            <div>
              <span>
                ملغاة
              </span>

              <strong>
                {formatNumber(
                  dashboard.orders
                    .statuses
                    .cancelled,
                )}
              </strong>
            </div>

            <div>
              <span>
                مرفوضة
              </span>

              <strong>
                {formatNumber(
                  dashboard.orders
                    .statuses
                    .rejected,
                )}
              </strong>
            </div>
          </div>

          <div className="dashboard-progress-block">
            <div className="dashboard-progress-label">
              <span>
                نسبة الإكمال
              </span>

              <strong>
                {formatPercentage(
                  dashboard.orders
                    .completionRate,
                )}
              </strong>
            </div>

            <div className="dashboard-progress-track">
              <span
                style={{
                  width: `${Math.min(
                    dashboard.orders
                      .completionRate,
                    100,
                  )}%`,
                }}
              />
            </div>
          </div>
        </article>
      </section>

      {/* ===================================================
          CHARTS
      =================================================== */}

      <section className="dashboard-charts-grid">
        {/* Users */}

        <article className="dashboard-chart-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                نمو المستخدمين
              </h2>

              <p>
                التسجيلات الجديدة والنشاط.
              </p>
            </div>

            <span className="section-header-icon">
              <FaUsers />
            </span>
          </div>

          <div className="dashboard-chart-body">
            {trendsLoading ? (
              <div className="chart-loading">
                جارٍ تحميل الرسم...
              </div>
            ) : (
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <AreaChart
                  data={
                    trends?.users ??
                    []
                  }
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="label"
                    tick={{
                      fontSize: 10,
                    }}
                  />

                  <YAxis
                    tick={{
                      fontSize: 10,
                    }}
                  />

                  <Tooltip />

                  <Legend />

                  <Area
                    type="monotone"
                    dataKey="registrations"
                    name="تسجيلات جديدة"
                    stroke="#3d7585"
                    fill="#3d7585"
                    fillOpacity={0.12}
                    strokeWidth={2}
                  />

                  <Area
                    type="monotone"
                    dataKey="activeUsers"
                    name="مستخدمون نشطون"
                    stroke="#47a778"
                    fill="#47a778"
                    fillOpacity={0.08}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </article>

        {/* Orders */}

        <article className="dashboard-chart-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                حركة الطلبات
              </h2>

              <p>
                الطلبات والمكتملة والملغاة.
              </p>
            </div>

            <span className="section-header-icon">
              <FaShoppingCart />
            </span>
          </div>

          <div className="dashboard-chart-body">
            {trendsLoading ? (
              <div className="chart-loading">
                جارٍ تحميل الرسم...
              </div>
            ) : (
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={
                    trends?.orders ??
                    []
                  }
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="label"
                    tick={{
                      fontSize: 10,
                    }}
                  />

                  <YAxis
                    tick={{
                      fontSize: 10,
                    }}
                  />

                  <Tooltip />

                  <Legend />

                  <Bar
                    dataKey="orders"
                    name="الطلبات"
                    fill="#607fc7"
                    radius={[
                      5,
                      5,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="completed"
                    name="المكتملة"
                    fill="#47a778"
                    radius={[
                      5,
                      5,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="cancelled"
                    name="الملغاة"
                    fill="#ca6875"
                    radius={[
                      5,
                      5,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </article>

        {/* Revenue */}

        <article className="dashboard-chart-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                قيمة الطلبات
              </h2>

              <p>
                حركة قيمة الطلبات حسب اليوم.
              </p>
            </div>

            <span className="section-header-icon">
              <MdOutlineTrendingUp />
            </span>
          </div>

          <div className="dashboard-chart-body">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <LineChart
                data={
                  trends?.orders ??
                  []
                }
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                />

                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: 10,
                  }}
                />

                <YAxis
                  tick={{
                    fontSize: 10,
                  }}
                />

                <Tooltip
                  formatter={(
                    value,
                  ) =>
                    formatIQD(
                      Number(value),
                    )
                  }
                />

                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="قيمة الطلبات"
                  stroke="#d78541"
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>

        {/* Serials */}

        <article className="dashboard-chart-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                استخدام السيريلات
              </h2>

              <p>
                عمليات الاستبدال والنقاط.
              </p>
            </div>

            <span className="section-header-icon">
              <FaBoxes />
            </span>
          </div>

          <div className="dashboard-chart-body">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <AreaChart
                data={
                  trends?.serials ??
                  []
                }
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                />

                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: 10,
                  }}
                />

                <YAxis
                  tick={{
                    fontSize: 10,
                  }}
                />

                <Tooltip />

                <Legend />

                <Area
                  type="monotone"
                  dataKey="redeemed"
                  name="السيريلات"
                  stroke="#526cbd"
                  fill="#526cbd"
                  fillOpacity={0.12}
                  strokeWidth={2}
                />

                <Area
                  type="monotone"
                  dataKey="points"
                  name="النقاط"
                  stroke="#c79932"
                  fill="#c79932"
                  fillOpacity={0.08}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </article>

        {/* Notifications */}

        <article className="dashboard-chart-card dashboard-chart-wide">
          <div className="dashboard-section-header">
            <div>
              <h2>
                أداء الإشعارات
              </h2>

              <p>
                مقارنة الإرسال الناجح والفاشل والمتخطى.
              </p>
            </div>

            <span className="section-header-icon">
              <FaBell />
            </span>
          </div>

          <div className="dashboard-chart-body">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <LineChart
                data={
                  trends?.notifications ??
                  []
                }
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                />

                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: 10,
                  }}
                />

                <YAxis
                  tick={{
                    fontSize: 10,
                  }}
                />

                <Tooltip />

                <Legend />

                <Line
                  type="monotone"
                  dataKey="sent"
                  name="نجح"
                  stroke="#47a778"
                  strokeWidth={2}
                  dot={false}
                />

                <Line
                  type="monotone"
                  dataKey="failed"
                  name="فشل"
                  stroke="#ca6875"
                  strokeWidth={2}
                  dot={false}
                />

                <Line
                  type="monotone"
                  dataKey="skipped"
                  name="متخطى"
                  stroke="#c79932"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>
      </section>

      {/* ===================================================
          MAP
      =================================================== */}

      <section className="dashboard-section-card dashboard-map-section">
        <div className="dashboard-section-header dashboard-map-header">
          <div className="dashboard-map-title">
            <span className="section-header-icon">
              <FaMapMarkedAlt />
            </span>

            <div>
              <h2>
                خريطة مستخدمي شوف
              </h2>

              <p>
                مواقع التقنيين والوكلاء والمستخدمين المسجلة في التطبيق.
              </p>
            </div>
          </div>

          <div className="dashboard-map-controls">
            <div className="dashboard-map-counter">
              <span>
                المواقع المعروضة
              </span>

              <strong>
                {formatNumber(
                  userLocations.length,
                )}
              </strong>
            </div>

            <div className="dashboard-province-filter">
              <label
                htmlFor="dashboard-province"
              >
                المحافظة
              </label>

              <select
                id="dashboard-province"
                value={
                  selectedProvince
                    ?.name ??
                  ""
                }
                onChange={event => {
                  const province =
                    iraqProvinces.find(
                      item =>
                        item.name ===
                        event.target
                          .value,
                    ) ??
                    null;

                  setSelectedProvince(
                    province,
                  );
                }}
              >
                <option value="">
                  كل العراق
                </option>

                {iraqProvinces.map(
                  province => (
                    <option
                      key={
                        province.name
                      }
                      value={
                        province.name
                      }
                    >
                      {
                        province.name
                      }
                    </option>
                  ),
                )}
              </select>
            </div>
          </div>
        </div>

        {mapError && (
          <div className="dashboard-map-error">
            {mapError}
          </div>
        )}

        {mapLoading ? (
          <div className="dashboard-map-loading">
            <div className="loading-spinner" />

            <span>
              جارٍ تحميل المواقع...
            </span>
          </div>
        ) : (
          <div className="dashboard-map-wrapper">
            <GoogleMapLoader>
              <UserLocationsMap
                userLocations={
                  userLocations
                }
                isLoaded
                activeTab="locations"
                filters={
                  mapFilters
                }
                handleFilterChange={
                  handleMapFilterChange
                }
                selectedProvince={
                  selectedProvince
                }
              />
            </GoogleMapLoader>
          </div>
        )}
      </section>

      {/* ===================================================
          Inventory / Serials / Points
      =================================================== */}

      <section className="dashboard-three-grid">
        <article className="dashboard-section-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                المخزون
              </h2>

              <p>
                الحالة الحالية للمنتجات.
              </p>
            </div>

            <span className="section-header-icon">
              <FaBoxOpen />
            </span>
          </div>

          <div className="vertical-stat-list">
            <div>
              <span>
                متوفر
              </span>

              <strong>
                {formatNumber(
                  dashboard.products
                    .inStock,
                )}
              </strong>
            </div>

            <div>
              <span>
                مخزون منخفض
              </span>

              <strong>
                {formatNumber(
                  dashboard.products
                    .lowStock,
                )}
              </strong>
            </div>

            <div>
              <span>
                نفد المخزون
              </span>

              <strong>
                {formatNumber(
                  dashboard.products
                    .outOfStock,
                )}
              </strong>
            </div>

            <div>
              <span>
                إجمالي الكمية
              </span>

              <strong>
                {formatNumber(
                  dashboard.products
                    .totalStockQuantity,
                )}
              </strong>
            </div>
          </div>
        </article>

        <article className="dashboard-section-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                السيريلات
              </h2>

              <p>
                حالة استخدام السيريلات.
              </p>
            </div>

            <span className="section-header-icon">
              <FaBoxes />
            </span>
          </div>

          <div className="vertical-stat-list">
            <div>
              <span>
                متاحة
              </span>

              <strong>
                {formatNumber(
                  dashboard.serials
                    .available,
                )}
              </strong>
            </div>

            <div>
              <span>
                مستخدمة
              </span>

              <strong>
                {formatNumber(
                  dashboard.serials
                    .redeemed,
                )}
              </strong>
            </div>

            <div>
              <span>
                محظورة
              </span>

              <strong>
                {formatNumber(
                  dashboard.serials
                    .blocked,
                )}
              </strong>
            </div>

            <div>
              <span>
                المستخدمة اليوم
              </span>

              <strong>
                {formatNumber(
                  dashboard.serials
                    .redeemedToday,
                )}
              </strong>
            </div>
          </div>
        </article>

        <article className="dashboard-section-card">
          <div className="dashboard-section-header">
            <div>
              <h2>
                النقاط
              </h2>

              <p>
                حركة نقاط المستخدمين.
              </p>
            </div>

            <span className="section-header-icon">
              <FaCoins />
            </span>
          </div>

          <div className="vertical-stat-list">
            <div>
              <span>
                أضيف اليوم
              </span>

              <strong>
                {formatNumber(
                  dashboard.points
                    .addedToday,
                )}
              </strong>
            </div>

            <div>
              <span>
                صُرف اليوم
              </span>

              <strong>
                {formatNumber(
                  dashboard.points
                    .deductedToday,
                )}
              </strong>
            </div>

            <div>
              <span>
                عمليات اليوم
              </span>

              <strong>
                {formatNumber(
                  dashboard.points
                    .transactionsToday,
                )}
              </strong>
            </div>

            <div>
              <span>
                عمليات 30 يوم
              </span>

              <strong>
                {formatNumber(
                  dashboard.points
                    .transactionsLast30Days,
                )}
              </strong>
            </div>
          </div>
        </article>
      </section>

      {error && (
        <div className="dashboard-inline-error">
          {error}
        </div>
      )}
    </main>
  );
};

export default Home;